import { useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'umi';
import { useMount, useRequest } from 'ahooks';
import moment from 'moment';
import { FormInstance } from 'antd';
import { Divider, Typography, Button } from 'iglooform';
import { useIntl } from 'react-intl';
import { getApplictionConfig } from './service';
import * as REGEXP from '@/configs/regexp';
import type { FormItemConfig } from 'iglooform/es/types';
import type { NamePath } from 'rc-field-form/es/interface';
import useBshHcAddableSection from './useBshHcAddableSection';
import useBshPaAddableSection from './useBshPaAddableSection';
import { cloneDeep } from 'lodash';
import {
  BSHHospitalCashProductKey,
  BSHPAProductKey,
  GICPAProductKey,
} from '@/constants/productKey';
import { message } from 'iglooform';
interface IConfig {
  config: {
    form: {
      default: FormItemConfig;
      languages: { [prop: string]: FormItemConfig };
    };
    schema: { [prop: string]: any };
  };
}

interface IUseGetFormConfig {
  (
    form: FormInstance,
    plan: { [prop: string]: any },
    fastQuote: { [prop: string]: any },
  ): {
    config: FormItemConfig;
    loading: boolean;
  };
}

const useGetFormConfig: IUseGetFormConfig = (form, plan, fastQuote) => {
  const { locale, formatMessage } = useIntl();
  const location = useLocation();
  const params = useParams<{ type: TTypes }>();

  const { data, run, loading } = useRequest<IConfig, any[]>(
    getApplictionConfig,
    {
      manual: true,
    },
  );

  const { productKey, planKey } = (location as any)?.query || {};

  const isBSHHc = productKey === BSHHospitalCashProductKey;
  const isBSHPa = productKey === BSHPAProductKey;
  const isGICPa = productKey === GICPAProductKey;

  useMount(() => {
    run({
      productType: params.type,
      productKey,
      planKey,
    });
  });

  const uploadImage = useCallback<(file: any) => any>((file) => {
    const isJpgOrPng = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp',
      'image/gif',
      'image/bmp',
      'image/ico',
      'image/svg',
      'image/pdf',
      'image/tiff',
    ].includes(file.type);
    if (!isJpgOrPng) {
      message.error(formatMessage({ id: 'You can only upload image file' }));
      return Promise.reject();
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('biz_key', 'ips-id');
    formData.append('is_public', 'false');
    formData.append('with_content', 'true');
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/v1/branch/fileapi/common_file/proxy/upload/turbo`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-Axinan-Authorization': localStorage.getItem(TOKEN_NAME) as string,
        },
      })
        .then((response) => response.json())
        .then((res) => {
          if (res?.url) {
            return resolve(res.url);
          }
          if (res?.message === 'exceed max size') {
            return reject(formatMessage({ id: 'Exceed max size' }));
          }
          throw Error('failed');
        })
        .catch((e) => reject(e.message || 'Upload image failed'));
    });
  }, []);

  const onPolicyStartTimeChange = (
    value: moment.Moment,
    namePath: NamePath,
    duration: 3 | 6 | 12 = 12,
  ) => {
    const nextYear = isGICPa
      ? value.add(1, 'year').subtract(1, 'day').endOf('day')
      : value.add(duration, 'month');
    form.setFields([
      {
        name: namePath,
        value: nextYear.valueOf(),
      },
    ]);
  };

  const dealUpload = useCallback<
    (formConfig: FormItemConfig) => FormItemConfig
  >(
    (formConfig) => {
      const { elements } = formConfig;
      function moreActionForForm(elements: FormItemConfig[] = []) {
        elements.map((config) => {
          const { type, elements: childElements } = config;
          if (['UploadPhoto', 'Upload'].includes(type as string)) {
            config.handleUpload = uploadImage;
            config.limit = 1;
          }
          moreActionForForm(childElements);
        });
      }
      moreActionForForm(elements);
      return formConfig;
    },
    [uploadImage],
  );

  const dealWithConfirmation = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.description = <></>;
    applicationConfig.confirmations = {
      promotions: {
        required: true,
        confirmation: {
          message: formatMessage(
            {
              id: 'I declare the information that I have provided above is true and agree to {link}.',
            },
            {
              link: (
                <a
                  style={{ fontStyle: 'italic' }}
                  target="_blank"
                  href="https://contents.iglooinsure.com/termsofservice"
                  rel="noreferrer"
                >
                  {formatMessage({
                    id: 'Terms & Conditions',
                  })}
                </a>
              ),
            },
          ),
        },
      },
    };
    return applicationConfig;
  }, []);

  const dealEmail = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map(
      (item, index) => {
        if (index === 0) {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (((e?.name || '') as string).includes('email')) {
                e.rules = [
                  {
                    validator: (_, value) =>
                      new Promise((resolve, reject) => {
                        if (
                          !REGEXP.regDiacriticsViAlphabetReg.test(value) ||
                          !value
                        ) {
                          resolve('ok');
                        } else {
                          reject(
                            new Error(
                              formatMessage({
                                id: 'Email should not contain diacritics alphabet.',
                              }),
                            ),
                          );
                        }
                      }),
                  },
                ];
              }
              return e;
            },
          );
        }
        return item;
      },
    );
    return applicationConfig;
  }, []);

  const dealWithInitialValues = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      const initialValueConfig = {
        motor: {
          vehicle_identification_type: 'Vehicle Plate Number',
          gender: 'male',
        },
        travel: {
          gender: 'male',
        },
        pa: {
          identification_type: 'Citizen ID',
        },
        health: {
          identification_type: 'Citizen ID',
          gender: 'male',
        },
      } as { [prop: string]: any };
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              const initValuesConfig = initialValueConfig[params.type];
              if (Object.keys(initValuesConfig).includes(e?.name as string)) {
                e.initialValue = initValuesConfig[e?.name as string];
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithPostalCode = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (e?.name === 'postal_code') {
                e.rules = [
                  {
                    validator: (_, value) =>
                      new Promise((resolve, reject) => {
                        if (
                          !value ||
                          value?.length > 8 ||
                          value?.length < 5 ||
                          REGEXP.regPostalCode.test(value)
                        ) {
                          resolve('ok');
                        } else {
                          reject(
                            new Error(
                              formatMessage({
                                id: 'Postal code can only enter numbers.',
                              }),
                            ),
                          );
                        }
                      }),
                  },
                ];
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithMobileNumber = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (
                ((e?.name || '') as string).includes('mobile_number') ||
                e?.name === 'owner_mobile_number'
              ) {
                e.rules = [
                  {
                    validator: (_, value) =>
                      new Promise((resolve, reject) => {
                        if (
                          !value?.phoneNumber ||
                          value?.phoneNumber?.length < 9 ||
                          value?.phoneNumber?.length > 11 ||
                          REGEXP.regMobileNumber.test(value?.phoneNumber)
                        ) {
                          resolve('ok');
                        } else {
                          reject(
                            new Error(
                              formatMessage({
                                id: 'Wrong telephone number format.',
                              }),
                            ),
                          );
                        }
                      }),
                  },
                ];
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithPassportNumber = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (['passport_number', 'passport'].includes(e?.name as string)) {
                e.rules = [
                  {
                    validator: (_, value) =>
                      new Promise((resolve, reject) => {
                        if (!value || REGEXP.regPassportNumber.test(value)) {
                          resolve('ok');
                        } else {
                          reject(
                            new Error(
                              formatMessage({
                                id: 'Passport is with 8 or 9 characters include Alphabet letter and number.',
                              }),
                            ),
                          );
                        }
                      }),
                  },
                ];
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithCitizenID = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (e?.name === 'id_number') {
                e.rules = [
                  {
                    validator: (_, value) =>
                      new Promise((resolve, reject) => {
                        if (!value || REGEXP.regCitizenID.test(value)) {
                          resolve('ok');
                        } else {
                          reject(
                            new Error(
                              formatMessage({
                                id: 'Invalid ID Number format.',
                              }),
                            ),
                          );
                        }
                      }),
                  },
                ];
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithCitizenIDOrPassportNumber = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map(
      (item, index) => {
        (item as { [prop: string]: any }).elements = item?.elements?.map(
          (e) => {
            if (((e?.name || '') as string).includes('id_number_or_passport')) {
              e.rules = [
                {
                  validator: (_, value) =>
                    new Promise((resolve, reject) => {
                      if (
                        !value ||
                        REGEXP.regCitizenID.test(value) ||
                        REGEXP.regPassportNumber.test(value)
                      ) {
                        resolve('ok');
                      } else {
                        reject(
                          new Error(
                            formatMessage({
                              id: 'The format is not correct.',
                            }),
                          ),
                        );
                      }
                    }),
                },
              ];
            }
            return e;
          },
        );
        return item;
      },
    );
    return applicationConfig;
  }, []);

  const dealWithDOB = useCallback<
    (applicationConfig: FormItemConfig, name: string) => FormItemConfig
  >(
    (applicationConfig, name) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e: any) => {
              if ((e?.name || '').includes(name)) {
                if (e.relativeRangeStart?.type === 'year') {
                  const relativeRangeStartQuantity =
                    Math.abs(e.relativeRangeStart?.quantity) + 1;
                  const todayStart = moment().startOf('day');
                  const earliestStart = moment()
                    .startOf('day')
                    .subtract(relativeRangeStartQuantity, 'year')
                    .add(1, 'day');
                  const quantity = earliestStart.diff(todayStart, 'day');
                  e.relativeRangeStart = {
                    type: 'day',
                    quantity,
                  };
                }
              }
              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealWithDateLimit = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          (item as { [prop: string]: any }).elements = item?.elements?.map(
            (e) => {
              if (e?.dateLimitationType) {
                const {
                  dateLimitationType,
                  relativeRangeEnd,
                  absoluteRangeEnd,
                  relativeRangeStart,
                  absoluteRangeStart,
                } = e;
                const now = +moment();
                let rangeStart = 0;
                let rangeEnd = Number.MAX_SAFE_INTEGER;

                if (dateLimitationType === 'absolute' && absoluteRangeStart) {
                  rangeStart = +moment(absoluteRangeStart).startOf('day');
                }

                if (
                  dateLimitationType === 'relative' &&
                  relativeRangeStart?.quantity !== undefined
                ) {
                  rangeStart = +moment()
                    .add(relativeRangeStart.quantity, relativeRangeStart.type)
                    .startOf('day');
                }

                if (dateLimitationType === 'absolute' && absoluteRangeEnd) {
                  rangeEnd = +moment(absoluteRangeEnd).endOf('day');
                }

                if (
                  dateLimitationType === 'relative' &&
                  relativeRangeEnd?.quantity !== undefined
                ) {
                  rangeEnd = +moment()
                    .add(relativeRangeEnd.quantity, relativeRangeEnd.type)
                    .endOf('day');
                }

                if (now > rangeEnd) {
                  e.defaultPickerValue = moment(rangeEnd);
                }
                if (now < rangeStart) {
                  e.defaultPickerValue = moment(rangeStart);
                }

                // iglooform-bug单(https://axinan.atlassian.net/jira/software/projects/GO/boards/112/backlog?selectedIssue=GO-561)
                // 1.传来的只有relativeRangeEnd有值，relativeRangeStart为空对象时，设置relativeRangeStart为最小安全值，不然只能选到1970.1.1
                // 2.传来的relativeRangeEnd和relativeRangeStart都为空对象时，设置relativeRangeStart为最小安全值，不然只能选到1970.1.1
                if (
                  dateLimitationType === 'relative' &&
                  relativeRangeStart?.quantity === undefined
                ) {
                  e.relativeRangeStart = {
                    type: 'day',
                    quantity: Number.MIN_SAFE_INTEGER,
                  };
                }
              }

              return e;
            },
          );
          return item;
        },
      );
      return applicationConfig;
    },
    [params.type],
  );

  const dealMotorFiledsRegExp = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map(
      (item, index) => {
        item.elements = item?.elements?.map((e, i) => {
          if (e?.name === 'plate_number') {
            e.placeholder = formatMessage({
              id: 'eg: 59F1-12345',
            });
            e.style = { textTransform: 'uppercase' };
            e.rules = [
              {
                validator: (_, value) =>
                  new Promise((resolve, reject) => {
                    if (REGEXP.regVehiclePlate.test(value) || !value) {
                      resolve('ok');
                    } else {
                      reject(
                        new Error(
                          formatMessage({
                            id: 'Please use "-" to divide left and right sections, the left section must be within 3-5 characters, the right section must be within 4-6 characters.',
                          }),
                        ),
                      );
                    }
                  }),
              },
            ];
          }
          if (e?.name === 'chassis_number') {
            e.style = { textTransform: 'uppercase' };
            e.rules = [
              {
                validator: (_, value) =>
                  new Promise((resolve, reject) => {
                    if (REGEXP.regVehicleChassisNumber.test(value) || !value) {
                      resolve('ok');
                    } else {
                      reject(
                        new Error(
                          formatMessage({
                            id: 'The vehicle chassis number should be 17 digit or letter.',
                          }),
                        ),
                      );
                    }
                  }),
              },
            ];
          }
          if (e?.name === 'engine_number') {
            e.style = { textTransform: 'uppercase' };
            e.rules = [
              {
                validator: (_, value) =>
                  new Promise((resolve, reject) => {
                    if (REGEXP.regVehicleEngineNumber.test(value) || !value) {
                      resolve('ok');
                    } else {
                      reject(
                        new Error(
                          formatMessage({
                            id: 'The vehicle engine number should be 6-30 characters.',
                          }),
                        ),
                      );
                    }
                  }),
              },
            ];
          }
          return e;
        });
        return item;
      },
    );
    return applicationConfig;
  }, []);

  const dealWithPolicyDate = useCallback<
    (
      applicationConfig: FormItemConfig,
      namePath: NamePath,
      duration?: 3 | 6 | 12,
    ) => FormItemConfig
  >((applicationConfig, namePath, duration) => {
    applicationConfig.elements = applicationConfig?.elements?.map(
      (item, index) => {
        item.elements = item?.elements?.map((e, i) => {
          if (e?.name === 'policy_start_date') {
            if (isGICPa) {
              e.getValueFromEvent = (value) => +value.startOf('day');
            }
            e.onChange = (value: moment.Moment) =>
              onPolicyStartTimeChange(value, namePath, duration);
          }
          if (e?.name === 'policy_end_date') {
            e.disabled = true;
          }
          return e;
        });
        return item;
      },
    );
    return applicationConfig;
  }, []);

  const dealOptionPreview = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map((item) => {
      (item as { [prop: string]: any }).elements = item?.elements?.map((e) => {
        if (e.options) {
          e.previewFormater = (val: any) => {
            // 之前某些产品配置不规范，如返的type是’phoneNumer‘，但是属性里有options，下面兼容了一下
            return (
              (e.options as any).find((ele: any) => ele.value === val)?.label ||
              (val?.phoneNumber ? `${val?.areaCode}${val?.phoneNumber}` : val)
            );
          };
        }
        return e;
      });
      return item;
    });
    return applicationConfig;
  }, []);

  const {
    addableArr,
    updateSelectedSelf,
    copySelfValue,
    toAdditionalInsuredStep,
    clearNotSelfFields,
    add,
    remove,
    validationInsured,
  } = useBshHcAddableSection(form);

  const dealWithBshHcAddableSection = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          if (item.name === 'additional_insured') {
            const configItems =
              (item.elements || [])[0]?.elements || item.elements;
            const subTitle = (item.elements || [])[0]?.label;
            item.elements = [];

            const lenIsGt20 = addableArr.length >= 20;
            // 遍历
            addableArr.forEach((addableArrItem, idx) => {
              const { key, selectedSelf } = addableArrItem;
              const isFirst = idx === 0;

              // 单独处理每个configItems
              const newConfigItem = configItems?.map((v, vIdx) => {
                const commonConfig = {
                  ...v,
                  name: `${v.name}_${key}`,
                  disabled: {
                    field: ['additional_insured', `relationship_${key}`],
                    operation: 'eq',
                    value: 'self',
                  },
                };
                if (v.name === 'relationship') {
                  return {
                    ...commonConfig,
                    onChange: (v: string) => {
                      updateSelectedSelf(key, v === 'self');
                      if (v === 'self') {
                        copySelfValue(key);
                      } else {
                        clearNotSelfFields(key);
                      }
                    },
                    initialValue: selectedSelf ? 'self' : undefined,
                  };
                } else {
                  return {
                    ...commonConfig,
                    // 更改默认的select监听依赖, 联动项目前配置的是挨着的，所以取vIdx - 1即上一项的name值
                    dependField:
                      v.datasourceKey && v.dependField
                        ? [
                            'additional_insured',
                            `${configItems[vIdx - 1].name}_${key}`,
                          ]
                        : undefined,
                  };
                }
              });

              const pushList = [
                !isFirst && { render: () => <Divider style={{ margin: 0 }} /> },
                {
                  hideWhenPreview: true,
                  render: () => {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography level="h3b">
                          {subTitle} {idx + 1}
                        </Typography>
                        <Button onClick={() => remove(key)} type="link">
                          {formatMessage({ id: 'Remove' })}
                        </Button>
                      </div>
                    );
                  },
                },
                {
                  hideWhenEdit: true,
                  render: () => (
                    <Typography level="h3b">
                      {subTitle} {idx + 1}
                    </Typography>
                  ),
                },
                ...newConfigItem!,
              ].filter(Boolean) as any;
              item.elements!.push(...pushList);
            });
            // 添加add按钮
            item.elements = [
              ...item.elements,
              {
                hideWhenPreview: true,
                render: () =>
                  lenIsGt20 ? null : (
                    <Button onClick={() => add()} style={{ minWidth: 221 }}>
                      {formatMessage({ id: 'Add Insured' })}
                    </Button>
                  ),
              },
            ];
          }
          return item;
        },
      );
      return applicationConfig;
    },
    [addableArr],
  );

  const {
    addableArr: addableArrPa,
    updateSelectedSelf: updateSelectedSelfPa,
    copySelfValue: copySelfValuePa,
    toAdditionalInsuredStep: toAdditionalInsuredStepPa,
    clearNotSelfFields: clearNotSelfFieldsPa,
    add: addPa,
    remove: removePa,
    validationInsured: validationInsuredPa,
  } = useBshPaAddableSection(form);

  const dealWithBshPaAddableSection = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >(
    (applicationConfig) => {
      applicationConfig.elements = applicationConfig?.elements?.map(
        (item, index) => {
          if (item.name === 'additional_insured') {
            const configItems =
              (item.elements || [])[0]?.elements || item.elements;
            const subTitle = (item.elements || [])[0]?.label;
            item.elements = [];

            const lenIsGt20 = addableArrPa.length >= 20;
            const lenIsGt4 = addableArrPa.length >= 4;

            const isFamily = fastQuote?.applicants === 'Family';
            const isMax = isFamily ? lenIsGt4 : lenIsGt20;
            // 遍历
            addableArrPa.forEach((addableArrPaItem, idx) => {
              const { key, selectedSelf } = addableArrPaItem;
              const isFirst = idx === 0;

              // 单独处理每个configItems
              const newConfigItem = configItems?.map((v, vIdx) => {
                const commonConfig = {
                  ...v,
                  name: `${v.name}_${key}`,
                  disabled: {
                    field: ['additional_insured', `relationship_${key}`],
                    operation: 'eq',
                    value: 'self',
                  },
                };
                if (v.name === 'relationship') {
                  return {
                    ...commonConfig,
                    onChange: (v: string) => {
                      updateSelectedSelfPa(key, v === 'self');
                      if (v === 'self') {
                        copySelfValuePa(key);
                      } else {
                        clearNotSelfFieldsPa(key);
                      }
                    },
                    initialValue: selectedSelf ? 'self' : undefined,
                    options: isFamily
                      ? ((v.options || []) as any[]).filter((item: any) =>
                          ['self', 'wife_husband', 'child'].includes(
                            item.value,
                          ),
                        )
                      : v.options,
                  };
                } else {
                  return {
                    ...commonConfig,
                    // 更改默认的select监听依赖, 联动项目前配置的是挨着的，所以取vIdx - 1即上一项的name值
                    dependField:
                      v.datasourceKey && v.dependField
                        ? [
                            'additional_insured',
                            `${configItems[vIdx - 1].name}_${key}`,
                          ]
                        : undefined,
                  };
                }
              });

              const pushList = [
                !isFirst && { render: () => <Divider style={{ margin: 0 }} /> },
                {
                  hideWhenPreview: true,
                  render: () => {
                    return (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography level="h3b">
                          {subTitle} {idx + 1}
                        </Typography>
                        <Button onClick={() => removePa(key)} type="link">
                          {formatMessage({ id: 'Remove' })}
                        </Button>
                      </div>
                    );
                  },
                },
                {
                  hideWhenEdit: true,
                  render: () => (
                    <Typography level="h3b">
                      {subTitle} {idx + 1}
                    </Typography>
                  ),
                },
                ...newConfigItem!,
              ].filter(Boolean) as any;
              item.elements!.push(...pushList);
            });
            // 添加add按钮
            item.elements = [
              ...item.elements,
              {
                hideWhenPreview: true,
                render: () =>
                  isMax ? null : (
                    <Button onClick={() => addPa()} style={{ minWidth: 221 }}>
                      {formatMessage({ id: 'Add Insured' })}
                    </Button>
                  ),
              },
            ];
          }
          return item;
        },
      );
      return applicationConfig;
    },
    [addableArrPa, fastQuote?.applicant],
  );

  // 支持目前的动态下拉preview回显(city, district, ward)
  const cityDict = useRef({});
  const districtDict = useRef({});
  const wardDict = useRef({});
  const dealDynamicOptionPreview = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map((item) => {
      (item as { [prop: string]: any }).elements = item?.elements?.map((e) => {
        if (
          (e.datasourceKey && ((e.name || '') as string).includes('city')) ||
          ((e.name || '') as string).includes('district') ||
          ((e.name || '') as string).includes('ward')
        ) {
          // 记录入字典值
          e.onChange = (_: any, obj: any) => {
            if (((e.name || '') as string).includes('city')) {
              cityDict.current = {
                ...cityDict.current,
                [obj?.value]: obj?.label,
              };
            }
            if (((e.name || '') as string).includes('district')) {
              districtDict.current = {
                ...districtDict.current,
                [obj?.value]: obj?.label,
              };
            }
            if (((e.name || '') as string).includes('ward')) {
              wardDict.current = {
                ...wardDict.current,
                [obj?.value]: obj?.label,
              };
            }
          };
          // 回显出来
          e.previewFormater = (v: string) => {
            if (((e.name || '') as string).includes('city')) {
              return (cityDict.current as any)[v] || v;
            }
            if (((e.name || '') as string).includes('district')) {
              return (districtDict.current as any)[v] || v;
            }
            if (((e.name || '') as string).includes('ward')) {
              return (wardDict.current as any)[v] || v;
            }
          };
        }
        return e;
      });
      return item;
    });
    return applicationConfig;
  }, []);

  const dealBithDateTo12 = useCallback<
    (applicationConfig: FormItemConfig) => FormItemConfig
  >((applicationConfig) => {
    applicationConfig.elements = applicationConfig?.elements?.map((item) => {
      (item as { [prop: string]: any }).elements = item?.elements?.map((e) => {
        if (
          ((e.name || '') as string).includes('birth_date') ||
          ((e.name || '') as string).includes('date_of_birth')
        ) {
          e.getValueFromEvent = (value) =>
            +moment(value).set({ hour: 12, minute: 0, second: 0 });
        }
        return e;
      });
      return item;
    });
    return applicationConfig;
  }, []);

  const config = useMemo(() => {
    let dataConfig =
      locale === 'en-US'
        ? data?.config?.form?.default!
        : data?.config?.form?.languages[locale];
    if (!dataConfig) return;
    let formConfig = cloneDeep(dataConfig);
    formConfig = dealUpload(formConfig);
    formConfig = dealWithConfirmation(formConfig);
    formConfig = dealEmail(formConfig);
    formConfig = dealWithInitialValues(formConfig);
    formConfig = dealWithPostalCode(formConfig);
    formConfig = dealWithMobileNumber(formConfig);
    formConfig = dealWithDateLimit(formConfig);
    formConfig = dealOptionPreview(formConfig);
    formConfig = dealDynamicOptionPreview(formConfig);
    formConfig = dealBithDateTo12(formConfig);

    if (params.type === 'motor') {
      formConfig = dealMotorFiledsRegExp(formConfig);
    }

    if (params.type === 'pa') {
      if (isBSHPa) {
        formConfig = dealWithBshPaAddableSection(formConfig);
        // 经过dealWithBshPaAddableSection处理后，再进行一些基础的格式化操作
        formConfig = dealWithDOB(formConfig, 'birth_date');
        formConfig = dealWithMobileNumber(formConfig);
        formConfig = dealWithDateLimit(formConfig);
        formConfig = dealOptionPreview(formConfig);
        formConfig = dealWithCitizenIDOrPassportNumber(formConfig);
        formConfig = dealDynamicOptionPreview(formConfig);
        formConfig = dealBithDateTo12(formConfig);
      } else {
        formConfig = dealWithPassportNumber(formConfig);
        formConfig = dealWithCitizenID(formConfig);
        formConfig = dealWithDOB(formConfig, 'birth_date');
      }
      formConfig = dealWithPolicyDate(formConfig, [
        'policy',
        'policy_end_date',
      ]);
    }

    if (params.type === 'travel') {
      formConfig = dealWithPassportNumber(formConfig);
      formConfig = dealWithDOB(formConfig, 'date_of_birth');
    }

    if (params.type === 'health') {
      if (fastQuote?.policy_duration) {
        if (isBSHHc) {
          formConfig = dealWithBshHcAddableSection(formConfig);
          // 经过dealWithBshHcAddableSection处理后，再进行一些基础的格式化操作
          formConfig = dealWithDOB(formConfig, 'birth_date');
          formConfig = dealWithMobileNumber(formConfig);
          formConfig = dealWithDateLimit(formConfig);
          formConfig = dealOptionPreview(formConfig);
          formConfig = dealWithCitizenIDOrPassportNumber(formConfig);
          formConfig = dealDynamicOptionPreview(formConfig);
          formConfig = dealBithDateTo12(formConfig);
        } else {
          formConfig = dealWithPassportNumber(formConfig);
          formConfig = dealWithCitizenID(formConfig);
          formConfig = dealWithDOB(formConfig, 'birth_date');
        }
        formConfig = dealWithPolicyDate(
          formConfig,
          ['policy', 'policy_end_date'],
          Number((fastQuote.policy_duration || '').split(' ')[0]) as any,
        );
      }
    }

    formConfig?.elements?.forEach((ele, index) => {
      ele.buttonText = formatMessage({ id: 'Next' });
      ele.handleNext = (value: any) => {
        // 埋点：Application step
        window.gtag('event', `application_step${index + 2}`, {
          product_key: (location as any)?.query?.productKey,
          plan_key: (location as any)?.query?.planKey,
          quote_id: (location as any)?.query?.quotationId,
          user_unique_id: JSON.parse(localStorage.getItem('user_info') ?? '{}')
            ?.id,
        });
        // hc中bsh产品特殊逻辑(continue按钮)
        if (isBSHHc) {
          if (ele.name === 'applicant') {
            toAdditionalInsuredStep();
          }
          if (ele.name === 'additional_insured') {
            return validationInsured(value);
          }
        }
        // pa中bsh产品特殊逻辑(continue按钮)
        if (isBSHPa) {
          if (ele.name === 'applicant') {
            toAdditionalInsuredStepPa();
          }
          if (ele.name === 'additional_insured') {
            return validationInsuredPa(value);
          }
        }
      };
    });

    return formConfig;
  }, [
    locale,
    data,
    dealUpload,
    params.type,
    plan,
    fastQuote,
    dealWithBshHcAddableSection,
    dealDynamicOptionPreview,
    dealWithBshPaAddableSection,
  ]);

  return {
    config: config!,
    loading,
  };
};

export default useGetFormConfig;
